/* ===========================================================
   SoHo Matcha — Window-QR Landing Page (client logic)
   Asset path: assets/soho-matcha.js

   Owns the whole single-page flow with no full reloads:
   intent -> capture -> reveal/redeem, plus open/closed + hours.

   EDGE-CACHE RULE: Nostra caches the HTML at the edge, so open/closed
   and the reveal MUST be decided here, at runtime — never in Liquid.
   Store-local time is computed explicitly in America/New_York; the
   device timezone is never trusted.

   No private API key lives here. Klaviyo is called with the public
   company ID only. The Phase-2 unique code is fetched from a server
   endpoint (which holds the key); if that endpoint is unset or errors,
   the reveal degrades to the emailed-code message.
   =========================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-soho-root]');
  if (!root) return;

  // ---- Config -------------------------------------------------------------
  var cfg = {};
  try {
    var el = root.querySelector('[data-soho-config]');
    cfg = JSON.parse(el.textContent);
  } catch (e) { cfg = {}; }
  cfg.klaviyo = cfg.klaviyo || {};
  cfg.offers = cfg.offers || { latte: {}, tin: {} };
  cfg.sourceTag = cfg.sourceTag || { open: 'soho_window_2026', closed: 'soho_window_afterhours' };
  cfg.hours = cfg.hours || [];
  cfg.holidays = cfg.holidays || [];

  // ---- Paid attribution ---------------------------------------------------
  // A ?src= URL param lets paid ads (Meta, etc.) tag their traffic so it stays
  // distinguishable from a physical window scan. It overrides the profile
  // custom_source, the hs_source event property, and the GA source dimension.
  // Untrusted input, so it is sanitized to a slug before touching any of those.
  // The METRIC name ("SoHo Window Scan") is deliberately NOT split by source:
  // one metric keeps the DTC flow trigger and the dashboards whole; attribution
  // lives in the event PROPERTY, not the metric. Absent/invalid param => the
  // existing window behavior is unchanged (falls back to sourceTag / 'window').
  cfg.paidSource = (function () {
    try {
      var raw = new URLSearchParams(window.location.search).get('src') || '';
      return raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
    } catch (e) { return ''; }
  })();

  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  var state = { intent: null, offerType: null, open: false, code: '', issuedAt: '' };

  // ---- Small helpers ------------------------------------------------------
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function show(node) { if (node) node.hidden = false; }
  function hide(node) { if (node) node.hidden = true; }
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function track(name, params) {
    // Every GA event carries the source dimension: the paid slug when present, else 'window'.
    var p = assign({ source: cfg.paidSource || 'window' }, params);
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(assign({ event: name }, p)); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', name, p); } catch (e) {}
  }
  function assign(a, b) { a = a || {}; b = b || {}; for (var k in b) if (Object.prototype.hasOwnProperty.call(b, k)) a[k] = b[k]; return a; }
  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  // ---- Store-local time (America/New_York), device tz ignored -------------
  function nyNow() {
    var parts = {};
    try {
      new Intl.DateTimeFormat('en-US', {
        timeZone: cfg.storeTz || 'America/New_York', hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit'
      }).formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    } catch (e) {
      var d = new Date();
      parts = { year: d.getFullYear() + '', month: pad(d.getMonth() + 1), day: pad(d.getDate()), weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()], hour: pad(d.getHours()), minute: pad(d.getMinutes()) };
    }
    var map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    var hour = parseInt(parts.hour, 10); if (hour >= 24) hour = 0;
    return {
      dateStr: parts.year + '-' + parts.month + '-' + parts.day,
      weekday: map[parts.weekday],
      minutes: hour * 60 + parseInt(parts.minute, 10)
    };
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function hhmmToMin(s) {
    if (!s || typeof s !== 'string' || s.indexOf(':') < 0) return null;
    var b = s.split(':'); var h = parseInt(b[0], 10); var m = parseInt(b[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }
  function fmtTime(min) {
    if (min == null) return '';
    var h = Math.floor(min / 60), m = min % 60;
    var ap = h >= 12 ? 'PM' : 'AM'; var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + (m ? ':' + pad(m) : '') + ' ' + ap;
  }
  // Resolve a given weekday index / date to its hours ({open,close} minutes) or null (closed)
  function hoursForDate(dateStr, weekday) {
    var hol = null;
    for (var i = 0; i < cfg.holidays.length; i++) { if (cfg.holidays[i] && cfg.holidays[i].date === dateStr) { hol = cfg.holidays[i]; break; } }
    if (hol) {
      if (hol.closed) return null;
      var o = hhmmToMin(hol.open), c = hhmmToMin(hol.close);
      return (o != null && c != null) ? { open: o, close: c } : null;
    }
    for (var j = 0; j < cfg.hours.length; j++) {
      var h = cfg.hours[j];
      if (h && Number(h.day) === weekday) {
        if (h.closed) return null;
        var oo = hhmmToMin(h.open), cc = hhmmToMin(h.close);
        return (oo != null && cc != null) ? { open: oo, close: cc } : null;
      }
    }
    return null;
  }
  function computeOpen() {
    var now = nyNow();
    var today = hoursForDate(now.dateStr, now.weekday);
    var isOpen = !!(today && now.minutes >= today.open && now.minutes < today.close);
    return { open: isOpen, now: now, today: today };
  }
  // Next opening moment, as friendly text, for the closed message
  function nextOpenText(now) {
    // If earlier today than open time, it's today.
    var today = hoursForDate(now.dateStr, now.weekday);
    if (today && now.minutes < today.open) return 'We open today at ' + fmtTime(today.open) + '.';
    for (var add = 1; add <= 7; add++) {
      var d = addDaysNY(now.dateStr, add);
      var h = hoursForDate(d.dateStr, d.weekday);
      if (h) {
        var when = add === 1 ? 'tomorrow' : WEEKDAYS[d.weekday];
        return 'We open ' + when + ' at ' + fmtTime(h.open) + '.';
      }
    }
    return '';
  }
  function addDaysNY(dateStr, add) {
    var p = dateStr.split('-');
    var base = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
    base.setUTCDate(base.getUTCDate() + add);
    var wd = base.getUTCDay();
    return { dateStr: base.getUTCFullYear() + '-' + pad(base.getUTCMonth() + 1) + '-' + pad(base.getUTCDate()), weekday: wd };
  }

  // ---- Hours renderer (fills every [data-soho-hours]) ---------------------
  function renderHours() {
    var st = computeOpen();
    var now = st.now;
    var rows = '';
    for (var wd = 0; wd < 7; wd++) {
      var isToday = wd === now.weekday;
      // Today's row is holiday-aware (so its range matches the Open/Closed pill); other rows show regular weekly hours.
      var h = isToday ? hoursForDate(now.dateStr, wd) : weekdayHours(wd);
      var label = h ? (fmtTime(h.open) + ' – ' + fmtTime(h.close)) : 'Closed';
      rows += '<div' + (isToday ? ' class="today"' : '') + '>' + WEEKDAYS[wd] + ': ' + label + (isToday ? ' · <span class="' + (st.open ? 'status-open">Open now' : 'status-closed">Closed now') + '</span>' : '') + '</div>';
    }
    // Fill EVERY hours target across all sibling sections — the Shop/Visit section lives outside [data-soho-root].
    Array.prototype.slice.call(document.querySelectorAll('[data-soho-hours]')).forEach(function (n) { n.innerHTML = rows; });
  }
  function weekdayHours(weekday) {
    for (var j = 0; j < cfg.hours.length; j++) {
      var h = cfg.hours[j];
      if (h && Number(h.day) === weekday) {
        if (h.closed) return null;
        var o = hhmmToMin(h.open), c = hhmmToMin(h.close);
        return (o != null && c != null) ? { open: o, close: c } : null;
      }
    }
    return null;
  }

  // ---- Klaviyo (public company ID only) -----------------------------------
  function klaviyoBase(path) { return 'https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(cfg.klaviyo.companyId || ''); }
  function klaviyoHeaders() { return { 'Content-Type': 'application/json', revision: cfg.klaviyo.revision || '2025-10-15' }; }

  function klaviyoSubscribe(email) {
    if (!cfg.klaviyo.companyId || !cfg.klaviyo.listId) return Promise.reject(new Error('klaviyo-not-configured'));
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: cfg.paidSource || 'SoHo Window 2026',
          profile: { data: { type: 'profile', attributes: {
            email: email,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: cfg.klaviyo.listId } } }
      }
    };
    return fetch(klaviyoBase('subscriptions'), { method: 'POST', headers: klaviyoHeaders(), body: JSON.stringify(body) })
      .then(function (r) { if (!r.ok && r.status !== 202) throw new Error('subscribe-' + r.status); return true; });
  }

  function klaviyoEvent(email, props) {
    if (!cfg.klaviyo.companyId) return Promise.resolve(false);
    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: props || {},
          metric: { data: { type: 'metric', attributes: { name: 'SoHo Window Scan' } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    };
    return fetch(klaviyoBase('events'), { method: 'POST', headers: klaviyoHeaders(), body: JSON.stringify(body) })
      .then(function (r) { return r.ok || r.status === 202; })
      .catch(function () { return false; }); // event is best-effort; never blocks the reveal
  }

  // ---- Live café pass (open redemption is staff-verified, not a code) ------
  // A ticking, store-local clock so a staffer can tell a genuine live screen from a screenshot;
  // the date catches a day-old screenshot. Open redemption = staff apply the value via POS quick-add.
  var passTimer = null;
  function passTick() {
    var clockEl = $('[data-soho-clock]');
    var dateEl = $('[data-soho-date]');
    var tz = cfg.storeTz || 'America/New_York';
    try {
      var p = {};
      new Intl.DateTimeFormat('en-US', { timeZone: tz, hour12: true, hour: 'numeric', minute: '2-digit', second: '2-digit' })
        .formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
      if (clockEl) clockEl.textContent = p.hour + ':' + p.minute + ':' + p.second + ' ' + (p.dayPeriod || '');
      if (dateEl) dateEl.textContent = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric' }).format(new Date());
    } catch (e) {
      if (clockEl) clockEl.textContent = new Date().toLocaleTimeString();
    }
  }
  function startPass() { stopPass(); passTick(); passTimer = setInterval(passTick, 1000); }
  function stopPass() { if (passTimer) { clearInterval(passTimer); passTimer = null; } }

  // ---- Flow ---------------------------------------------------------------
  var capturePanel = $('[data-soho-capture]');
  var revealPanel = $('[data-soho-reveal]');
  var previewLine = $('[data-soho-preview]');
  var form = $('[data-soho-form]');
  var emailInput = $('#soho-email');
  var consentInput = $('#soho-consent');
  var submitBtn = $('[data-soho-submit]');
  var errorEl = $('[data-soho-error]');

  var PREVIEW = { iced: 'Your iced matcha treat', hot: 'Your hot matcha treat', flavored: 'Your flavored matcha treat' };

  function selectIntent(intent) {
    state.intent = intent;
    // Re-selecting after a reveal restarts the flow cleanly — was leaving the reveal panel (and a
    // second hours block) stuck on screen.
    hide(revealPanel); stopPass(); setError('');
    $all('[data-intent]').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-intent') === intent ? 'true' : 'false'); });
    if (previewLine) previewLine.textContent = PREVIEW[intent] || '';
    // Tell the sibling education + shop blocks which variant to show (CSS keys off <html>).
    root.setAttribute('data-answer', intent);
    document.documentElement.setAttribute('data-soho-answer', intent);
    track('matcha_intent_selected', { value: intent });
    show(capturePanel);
    if (capturePanel) capturePanel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    if (emailInput) { try { emailInput.focus({ preventScroll: true }); } catch (e) {} }
  }

  $all('[data-intent]').forEach(function (btn) {
    btn.addEventListener('click', function () { selectIntent(btn.getAttribute('data-intent')); });
  });

  function setError(msg, emailInvalid) {
    if (!errorEl) return;
    errorEl.textContent = msg || '';
    // Only mark the email field invalid when the email itself failed — not for a consent/network error.
    if (emailInput) emailInput.setAttribute('aria-invalid', emailInvalid ? 'true' : 'false');
  }
  function setLoading(on) {
    if (!submitBtn) return;
    submitBtn.disabled = on;
    submitBtn.innerHTML = on ? '<span class="spin" aria-hidden="true"></span>Revealing…' : 'Reveal my treat';
  }

  if (form) form.addEventListener('submit', function (e) {
    e.preventDefault();
    setError('');
    var email = (emailInput && emailInput.value || '').trim();
    if (!validEmail(email)) { setError('Please enter a valid email address.', true); if (emailInput) emailInput.focus(); return; }
    if (consentInput && !consentInput.checked) { setError('Please check the box so we can send your treat, or tap “Just show me the café.”', false); if (consentInput) consentInput.focus(); return; }

    setLoading(true);

    var st = computeOpen();
    state.open = st.open;
    state.offerType = st.open ? 'cafe' : 'dtc';

    var proceed = cfg.simulateCapture ? Promise.resolve(true) : klaviyoSubscribe(email);

    proceed.then(function () {
      // Successful capture. OPEN redemption is a staff-verified live pass (no code). CLOSED/DTC is
      // email-only (a unique matcha-tin code from the Klaviyo flow), so the theme never handles a code.
      state.issuedAt = new Date().toISOString();
      var props = {
        hs_source: cfg.paidSource || (st.open ? cfg.sourceTag.open : cfg.sourceTag.closed),
        hs_matcha_level: state.intent,
        offer_type: state.offerType,
        hs_code_issued_at: state.issuedAt
      };
      if (!cfg.simulateCapture) klaviyoEvent(email, props);
      track('email_captured', { offer_type: state.offerType, state: st.open ? 'open' : 'closed' });
      goReveal(st.open);
    }).catch(function (err) {
      setLoading(false);
      var m = err && err.message;
      if (m === 'klaviyo-not-configured') {
        // Misconfiguration (IDs not set) — do not loop the visitor on a retry they cannot win.
        setError('Sign-up is not available yet. The café is in the back, past the tea wall.', false);
      } else {
        setError('We could not send your treat just now. Please try again.', false);
      }
      // eslint-disable-next-line no-console
      if (window.console && console.warn) console.warn('[soho] capture failed:', m);
    });
  });

  // Skip -> café, no capture
  var skipBtn = $('[data-soho-skip]');
  if (skipBtn) skipBtn.addEventListener('click', function () {
    track('skip_to_cafe', {});
    var target = document.getElementById('soho-visit') || $('.soho-wayfinding');
    if (target) target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  });

  function goReveal(isOpen) {
    hide(capturePanel);
    show(revealPanel);

    // OPEN shows the café matcha keyed to the answer (iced/hot/flavored) at the in-store pass amount (10%).
    // CLOSED/DTC is the emailed matcha-tin code, set higher (15%) to offset online shipping.
    var amountText = isOpen
      ? ((cfg.offers && cfg.offers.latte && cfg.offers.latte.amount) || '10% off')
      : ((cfg.offers && cfg.offers.dtc && cfg.offers.dtc.amount) || '15% off');

    var amountEl = $('[data-soho-amount]');
    if (amountEl) amountEl.innerHTML = decorateAmount(amountText);
    var kickerEl = $('[data-soho-kicker]');
    if (kickerEl) kickerEl.textContent = isOpen ? ('Your ' + state.intent + ' matcha treat') : 'Your matcha tin treat';

    var openBlock = $('[data-soho-open-block]');
    var closedBlock = $('[data-soho-closed-block]');

    if (isOpen) {
      show(openBlock); hide(closedBlock);
      startPass();   // live ticking clock so staff can tell a genuine pass from a screenshot
    } else {
      stopPass();
      hide(openBlock); show(closedBlock);
      var shopLink = $('[data-soho-shop-matcha]'); if (shopLink) shopLink.setAttribute('href', cfg.collectionUrl || '/collections/matcha');
      var nextEl = $('[data-soho-nextopen]'); if (nextEl) nextEl.textContent = nextOpenText(computeOpen().now);
    }

    renderHours();

    // Animation — respect reduced motion (value simply appears).
    var stage = $('[data-soho-stage]');
    if (stage) {
      if (reduceMotion) { stage.classList.add('is-revealed'); }
      else { requestAnimationFrame(function () { setTimeout(function () { stage.classList.add('is-revealed'); }, 60); }); }
    }
    revealPanel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    track('reward_revealed', { state: isOpen ? 'open' : 'closed', offer_type: state.offerType });
  }

  function decorateAmount(text) {
    // Style a leading $NN so the number is the hero; keep words small.
    return String(text).replace(/^(\$\d+(?:\.\d+)?)/, '$1').replace(/\b(off|any|matcha|tin|latte)\b/gi, '<span class="small">$1</span>');
  }

  // ---- Init ---------------------------------------------------------------
  renderHours();
  // Refresh open/closed + hours if the page sits open across a boundary.
  setInterval(renderHours, 60000);
})();
